import { FC, useEffect, useState } from "react";
import GridMultipleSelectorInput from "../../../../../../components/controls/GridMultipleSelectorInput"
import { IEntitySimpleDTO } from "../../../../../../libs/coreapi-dto/@types/common"
import { v4 as uuidv4 } from 'uuid';
import useGridFilter, { CreateSelectorGridFilter } from "../../../../../../system/hooks/useGridFilter"
import { useTranslation } from "react-i18next";
import { DefaultGrid } from "../../../../../../components/grids/default/defaultGrid"
import { ISysOptionsDTO } from "../../../../../../libs/coreapi-dto/service/sysOptions";
import { useAppContext } from "../../../../../../system/providers/appContextProvider";
import { ContractorDataProvider } from "../../../../../../Services/DataProviders/ContractorDataProvider";
import { IContractorViewDTO } from "../../../../../../libs/coreapi-dto/dirs/contractor";
import { ContractorSelectorModal } from "../../../../../Dictionaries/Contractor";
import SysOptionContractorPluginSettings from "./SysOptionContractorPluginSettings";
import { FieldsSource, IContractorsEditModalProps } from "./interfaces";

const ContractorsEditModal: FC<IContractorsEditModalProps> = (props) => {
    const appContext = useAppContext();
    const contractorDP = new ContractorDataProvider(appContext.coreApiService)

    const [selectedContractors, setSelectedContractors] = useState<IEntitySimpleDTO[]>([]);
    const [contractorsItems, setContractorsItems] = useState<IContractorViewDTO[]>([]);

    const [gridFilter] = useGridFilter(CreateSelectorGridFilter);
    const [gridFilterItems, dispatchGridFilterItems] = useGridFilter(CreateSelectorGridFilter);
    const [totalCount, setTotalCount] = useState(0);
    
    const { t } = useTranslation();
    const baseT = (value: string) => t("sysOptions." + value);

    useEffect(() => {
        if (props.initForContractors)
            initContractorsItems(props.initForContractors);
    }, [props.initForContractors]);

    function getJsonPropByKey(data: ISysOptionsDTO, key: string): any {
        const obj = JSON.parse(data?.jsonData);
        let objValue = obj.Value;
        let items = objValue[key];
        return items;
    }

    function setJsonPropByKey(data: ISysOptionsDTO, key: string, newValue: any): any {
        const obj = JSON.parse(data?.jsonData);
        let objValue = obj.Value;
        objValue[key] = newValue;
        return objValue;
    }

    function initContractorsItems(data: ISysOptionsDTO) {
        const items = getJsonPropByKey(data, props.fieldSource);
        
        const processContractorsData = (values: IContractorViewDTO[]) => {
            
            let filterValues = values.filter(x => 
                items.some((item: any) => item.GUID === x.idGlobal)
            );
            filterValues = sortContractorsByName(filterValues);
            
            setContractorsItems(filterValues);
            setTotalCount(filterValues.length);
            
            const selectedItems = mapToEntitySimpleDTO(filterValues);
            setSelectedContractors(selectedItems);
        };
    
        loadAllContractors(processContractorsData);
    }
    
    function getContractorsItems(entities: IEntitySimpleDTO[], data?: ISysOptionsDTO) {
        if (!data) return;
       
        const processContractorsData = (values: IContractorViewDTO[]) => {
            let filterValues = values.filter(x => entities.some(entity => entity.idGlobal === x.idGlobal));
            filterValues = sortContractorsByName(filterValues);
            
            setContractorsItems(filterValues);
            setTotalCount(filterValues.length);
            
            const contractors = extractAndSortNames(filterValues);
            updateSysOptionsData(data, contractors);
        };
    
        loadAllContractors(processContractorsData);
    }
    
    function sortContractorsByName(contractors: IContractorViewDTO[]): IContractorViewDTO[] {
        return [...contractors].sort((a, b) => {
            if (a.name && b.name) {
                return a.name.localeCompare(b.name);
            }
            return 0;
        });
    }
    
    function mapToEntitySimpleDTO(contractors: IContractorViewDTO[]): IEntitySimpleDTO[] {
        return contractors.map((x) => ({
            displayName: x.name?.toString() || '',
            idGlobal: x.idGlobal
        } as IEntitySimpleDTO));
    }
    
    function extractAndSortNames(contractors: IContractorViewDTO[]): any[] {
        return contractors.map(contractor => ({
          GUID: contractor.idGlobal
        }));
    }
    
    function updateSysOptionsData(data: ISysOptionsDTO, contractors: any[]) {
        const objValue = setJsonPropByKey(data, props.fieldSource, contractors);
        props.setData({
            ...data,
            jsonData: JSON.stringify({ Value: objValue })
        });
    }
    
    function loadAllContractors(callback: (values: IContractorViewDTO[]) => void) {
        contractorDP.getView(gridFilterItems, (values, totalCount) => {
            if (totalCount > gridFilterItems.numberPerPage) {
                const allDataFilter = { 
                    ...gridFilterItems, 
                    numberPerPage: totalCount, 
                    pageNumber: 1 
                };
                
                contractorDP.getView(allDataFilter, (allValues) => {
                    callback(allValues);
                });
            } else {
                callback(values);
            }
        });
    }

    function updateContractorsItems(entities: IEntitySimpleDTO[]) {
        getContractorsItems(entities, props.data);
    }

    const getLabel = () => {
        switch (props.fieldSource) {
            case FieldsSource.Suppliers:
                return baseT('Suppliers');
            case FieldsSource.Receivers:
                return baseT('Receivers');
            default:
                return baseT('Contractors');
        }
    }
    
    return (
        <div style={{ marginBottom: '20px' }}>
            <GridMultipleSelectorInput
                selectedEntity={selectedContractors?.[0]}
                selectedEntities={selectedContractors}
                id={`id${props.fieldSource}-${uuidv4()}`}
                switchOnchange
                gridFilter={gridFilter}
                selectorModalJsx={ContractorSelectorModal}
                label={getLabel()}
                onSelect={(entities: IEntitySimpleDTO[]) => {
                    setSelectedContractors(entities);
                    updateContractorsItems(entities);
                }}
                disabled={props.lockFromPermission || !props.isEnabled}
                isSearchForMultyselect
                treeViewCheckDirectoryType={'supplier'}
            />
            <DefaultGrid
                gridId={`grid-${props.fieldSource}-${uuidv4()}`}
                data={contractorsItems}
                filter={gridFilterItems}
                totalCount={totalCount}
                plugin={SysOptionContractorPluginSettings}
                selectedItem={undefined}
                onSelect={(row) => { }}
                onSort={(i) => { }}
                onFilterDelete={(i) => { dispatchGridFilterItems({ type: "deleteColumnFilter", payload: i.propertyName }) }}
                onPageNumberChange={(n) => { dispatchGridFilterItems({ type: "changePageNumber", payload: { pageNumber: n } }) }}
                onNumberPerPageChange={(n) => { dispatchGridFilterItems({ type: "changeNumberPerPage", payload: { numberPerPage: n } }) }}
                hiddenPagination={undefined}
                isSearchForMultyselect
            />
        </div>
    );
}

export default ContractorsEditModal;